import { useAuthenticationContext } from '@/Contexts/App/AuthenticationContext';
import { ThemeContext } from '@/Contexts/App/ThemeContext';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import { useUserContext } from '@/Contexts/Authenticated/UserContext';
import type { AboutInfo } from '@/Models/AboutInfo';
import { DobButton, DobIcon, DobTooltip } from '@dedalusdiit/dob-components';
import { useTheme } from '@mui/material';
import { t } from 'i18next';
import React, { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import * as API from '../../../API';
import AboutDialog from '../AboutDialog/AboutDialog';
import HeaderMenu from './HeaderMenu/HeaderMenu';
import Styles from './HeaderRight.module.scss';

const {
    headerRight,
    headerRightEntry,
    headerRightButtonsSection,
    headerRightUserMessage: userMessageStyle,
    headerRightUserMessageMessage: messageStyle,
} = Styles;

const LogOutButton = (): React.ReactElement => {
    const { logout: authLogout } = useAuthenticationContext();
    const { clearFilingCabinet } = useFilingCabinetContext();

    const logout = (): void => {
        authLogout();
        void clearFilingCabinet(true);
    };

    return (
        <DobButton color="silent" onClick={logout} size="small">{t('logout')}</DobButton>
    );
};

const ThemeSwitch = (): React.ReactElement => {
    const theme = useTheme();
    const themeContext = useContext(ThemeContext);
    const isDark = useMemo(() => theme.palette.mode === 'dark', [theme]);

    const iconName = isDark
        ? 'brightness-high-fill'
        : 'moon-fill';

    return (
        <DobButton icon={iconName} color="silent" onClick={() => themeContext.actions.toggleTheme()} />
    );
};

const UserMessage = (): React.ReactElement => {
    const { t } = useTranslation();
    const { user } = useUserContext();

    const {
        canEditUserDefaults,
        username,
        siteModel: {
            Name: siteName,
        },
    } = user;

    const toolTip = (
        <div>
            <p>
                <span>
                    <b>{t('UserName')}</b>:&nbsp;{username}
                </span>
            </p>
            <p>
                <span>
                    <b>{t('Site')}</b>
                    :
                    {' '}
                    {siteName}
                </span>
            </p>
            { canEditUserDefaults && (
                <p>
                    <i>{t('Header.UserMessage.OpenUserDefaults')}</i>
                </p>
            )}
        </div>
    );

    const message = (
        <DobTooltip content={toolTip}>
            <div className={userMessageStyle}>
                <div className={messageStyle}>
                    <DobIcon name="person-fill" size="1.5rem" />
                    <span>
                        <b>{user.fullName}</b>&nbsp;(<i>{siteName}</i>)
                    </span>
                </div>
            </div>
        </DobTooltip>
    );

    if (canEditUserDefaults) {
        return (
            <Link to="/UserDefaults">
                { message }
            </Link>
        );
    }

    return message;
};

const PreferredPrinter = (): React.ReactElement | undefined => {
    const { user } = useUserContext();

    if ((user.PreferredPrinter ?? '') === '') {
        return;
    }

    return (
        <div className="material-symbols-outlined" title={user.PreferredPrinter}>print</div>
    );
};

const PreferredScanner = (): React.ReactElement | undefined => {
    const { user } = useUserContext();

    if ((user.PreferredScanner ?? '') === '') {
        return;
    }

    return (
        <div className="material-symbols-outlined" title={user.PreferredScanner}>scanner</div>
    );
};

const createHTMLManualUrl = (userLanguage: string): URL => {
    return new URL(`manual/html/${userLanguage}/index.htm`, window.location.origin);
};

const createPDFManualUrl = (userLanguage: string): URL => {
    return new URL(`manual/pdf/RISonWEB_UserManual.${userLanguage}.pdf`, window.location.origin);
};

const HeaderMenuElement = (): React.ReactElement => {
    const { getUserLanguage } = useUserContext();
    const [popupData, setPopupData] = useState<AboutInfo | null>(null);
    const [showPopup, setShowPopup] = useState<boolean>(false);

    const userLanguage = getUserLanguage();

    const htmlManualURL = createHTMLManualUrl(userLanguage);
    const downloadPDFManualURL = createPDFManualUrl(userLanguage);

    const openROWInfo = (): void => {
        void API.general.getAboutInfo()
            .then((dict) => {
                setPopupData(dict);
                setShowPopup(true);
            });
    };

    return (
        <>
            <HeaderMenu
                options={[
                    {
                        type: 'url',
                        title: t('UserManualOnline'),
                        iconName: 'menu_book',
                        url: htmlManualURL,
                    },
                    {
                        type: 'url',
                        title: t('UserManualPDF'),
                        iconName: 'menu_book',
                        url: downloadPDFManualURL,
                    },
                    {
                        type: 'callback',
                        title: t('AboutRISonWEB'),
                        iconName: 'info',
                        onClick: () => openROWInfo(),
                    },
                ]}
            />
            {popupData !== null && (
                <AboutDialog open={showPopup} onClose={() => setShowPopup(false)} data={popupData} />
            )}
        </>
    );
};

const HeaderRight = (): React.ReactElement => {
    return (
        <div className={headerRight}>
            <div className={headerRightEntry}>
                <PreferredPrinter />
            </div>
            <div className={headerRightEntry}>
                <PreferredScanner />
            </div>
            <div className={headerRightEntry}>
                <UserMessage />
            </div>
            <div className={headerRightButtonsSection}>
                <HeaderMenuElement />
                <ThemeSwitch />
                <LogOutButton />
            </div>
        </div>
    );
};

export default HeaderRight;
